import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import type { ProxyUserOption, UserProxyCreate } from '../types'
import ProxyUserPicker from './ProxyUserPicker'

interface Props {
  visible: boolean
  submitting: boolean
  /** Sunucudan gelen alan/çakışma hatası (ör. aynı çiftte zaten aktif vekalet var). Boşsa gösterilmez. */
  serverError?: string | null
  onClearServerError: () => void
  onSubmit: (payload: UserProxyCreate) => void
  onHide: () => void
}

/**
 * **Yeni Vekalet** formu (090) — iki kişi alanı, başka alan yok.
 *
 * `useZodForm` (020) **kullanılMADI**: form iki nesne seçiminden oluşur ve tek kuralı "ikisi de dolu ve
 * birbirinden farklı"dır. Zod şeması bu iki koşulu tekrar tarif etmekten başka bir iş yapmaz, ama
 * `ProxyUserPicker`'ın nesne değerini `Controller` üzerinden bağlamak (serileşmeyen değer + `useEffect`
 * ile senkron) belirgin bir karmaşıklık eklerdi. Gönderim butonu geçerli olana kadar **kapalı** durur,
 * yani geçersiz gönderim mümkün değil.
 *
 * `GRANT_DATE` alanı **bilinçli olarak yoktur**: vekaletin ne zaman verildiği bir olgudur, girdi değil —
 * sunucu onu iş saatinden yazar.
 *
 * Kalan doğrulama sunucudadır ve orada da uygulanır (pasif kullanıcı → 400, aynı çiftte aktif kayıt →
 * 409): istemcinin bilmediği/eskiyen durumları yalnız sunucu görebilir.
 */
export default function UserProxyFormDialog({
  visible,
  submitting,
  serverError,
  onClearServerError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const [from, setFrom] = useState<ProxyUserOption | null>(null)
  const [to, setTo] = useState<ProxyUserOption | null>(null)

  // Her açılışta form boş başlar (düzenleme yok — form daima "yeni kayıt"tır).
  useEffect(() => {
    if (visible) {
      setFrom(null)
      setTo(null)
    }
  }, [visible])

  /** Aynı kişiyi iki alana seçmek anlamsızdır → istemcide de engellenir (sunucu 400 verir). */
  const sameUser = from != null && to != null && from.id === to.id
  const valid = from != null && to != null && !sameUser

  const pick = (setter: (u: ProxyUserOption | null) => void) => (u: ProxyUserOption | null) => {
    // Kullanıcı seçimi değiştirdiğinde bayat sunucu hatası ekranda kalmasın.
    if (serverError) onClearServerError()
    setter(u)
  }

  return (
    <FormDialog
      visible={visible}
      header={t('admin.userProxies.form.newTitle')}
      submitLabel={t('admin.userProxies.form.save')}
      submitting={submitting}
      submitDisabled={submitting || !valid}
      onSubmit={() => {
        if (valid) onSubmit({ fromUserId: from.id, toUserId: to.id })
      }}
      onHide={onHide}
    >
      <div className="admin-form">
        <div className="admin-field">
          <label>{t('admin.userProxies.columns.from')}</label>
          <ProxyUserPicker
            value={from}
            onChange={pick(setFrom)}
            placeholder={t('admin.userProxies.form.searchPlaceholder')}
            ariaLabel={t('admin.userProxies.columns.from')}
            autoFocus
          />
        </div>
        <div className="admin-field">
          <label>{t('admin.userProxies.columns.to')}</label>
          <ProxyUserPicker
            value={to}
            onChange={pick(setTo)}
            placeholder={t('admin.userProxies.form.searchPlaceholder')}
            ariaLabel={t('admin.userProxies.columns.to')}
          />
        </div>
        {/* İki alan aynı kişiyi gösteriyorsa neden gönderilemediği yazılır (buton sessizce kapanmaz). */}
        {sameUser && <p className="admin-proxy-error">{t('admin.userProxies.errors.sameUser')}</p>}
        {serverError && <p className="admin-proxy-error">{serverError}</p>}
      </div>
    </FormDialog>
  )
}
